import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Anchor, Home, User, BookOpen, PlusCircle, Shield, Menu, X, HelpCircle, MessageCircle, Search } from 'lucide-react';
import { Button } from '../ui/button';
import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '../ui/breadcrumb';
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from '../ui/sheet';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../ui/dropdown-menu';
import { useAuth } from '../../context/AuthContext';
import { useModuleAccess } from '../../hooks/useModuleAccess';
import NotificationBell from '../common/NotificationBell';
import Footer from './Footer';

const Layout = ({ children, hideBreadcrumbs = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAdmin } = useAuth();
  const { isModuleActive, isModuleHidden } = useModuleAccess();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  const isAdminOrSubAdmin = () => {
    return user && (user.role === 'admin' || user.role === 'sub_admin');
  };

  const getBreadcrumbs = () => {
    const path = location.pathname;
    
    // Breadcrumb mapping - each route maps to its trail
    const breadcrumbMap = {
      // Onboarding flow
      '/grade': [{ label: 'Select Grade', path: '/grade' }],
      '/mmd': [{ label: 'Select Grade', path: '/grade' }, { label: 'MMD Location', path: '/mmd' }],
      
      // Main user pages
      '/dashboard': [{ label: 'Dashboard', path: '/dashboard' }],
      '/question-bank': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Oral Question Bank', path: '/question-bank' }],
      '/written-bank': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Written Question Bank', path: '/written-bank' }],
      '/written-bank/all': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Written Question Bank', path: '/written-bank' }, { label: 'All Questions', path: '/written-bank/all' }],
      '/submit': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Submit Question', path: '/submit' }],
      '/profile': [{ label: 'Profile', path: '/profile' }],
      '/plans': [{ label: 'Plans', path: '/plans' }],
      '/checkout': [{ label: 'Plans', path: '/plans' }, { label: 'Checkout', path: '/checkout' }],
      
      // Forum
      '/forum': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Forum', path: '/forum' }],
      '/forum/new': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Forum', path: '/forum' }, { label: 'New Question', path: '/forum/new' }],
      '/admin/forum': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Forum Management', path: '/admin/forum' }],
      
      // Study features
      '/notebook': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Study Notebook', path: '/notebook' }],
      '/insights': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Exam Insights', path: '/insights' }],
      '/progress': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Progress Tracker', path: '/progress' }],
      
      // Practice flow
      '/practice': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Practice Sessions', path: '/practice' }],
      '/practice/session': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Practice Sessions', path: '/practice' }, { label: 'Session', path: '/practice/session' }],
      '/practice/results': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Practice Sessions', path: '/practice' }, { label: 'Results', path: '/practice/results' }],
      '/practice/written/session': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Practice Sessions', path: '/practice' }, { label: 'Written Session', path: '/practice/written/session' }],
      '/practice/written/results': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Practice Sessions', path: '/practice' }, { label: 'Written Results', path: '/practice/written/results' }],
      
      // Live Classes
      '/live-classes': [{ label: 'Dashboard', path: '/dashboard' }, { label: 'Live Classes', path: '/live-classes' }],
      '/admin/live-classes': [{ label: 'Admin', path: '/admin' }, { label: 'Live Classes', path: '/admin/live-classes' }],

      // Help & Info pages
      '/updates': [{ label: 'Updates', path: '/updates' }],
      '/help': [{ label: 'Help', path: '/help' }],
      '/help/docs': [{ label: 'Help', path: '/help' }, { label: 'Docs', path: '/help/docs' }],
      '/help/guides': [{ label: 'Help', path: '/help' }, { label: 'Guides', path: '/help/guides' }],
      // Old routes kept for backward compatibility — same crumbs.
      '/user-guide': [{ label: 'Help', path: '/help' }, { label: 'Docs', path: '/help/docs' }],
      '/guidelines': [{ label: 'Help', path: '/help' }, { label: 'Guides', path: '/help/guides' }],
      '/help/guidelines': [{ label: 'Help', path: '/help' }, { label: 'Guides', path: '/help/guides' }],
      '/contact-us': [{ label: 'Contact Us', path: '/contact-us' }],
      '/privacy-policy': [{ label: 'Privacy Policy', path: '/privacy-policy' }],
      '/terms-of-use': [{ label: 'Terms of Use', path: '/terms-of-use' }],
      '/refund-policy': [{ label: 'Refund Policy', path: '/refund-policy' }],
      
      // Admin pages
      '/admin': [{ label: 'Admin', path: '/admin' }],
      '/admin/content': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }],
      '/admin/review': [{ label: 'Admin', path: '/admin' }, { label: 'Review', path: '/admin/review' }],
      '/admin/questions': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Oral Management', path: '/admin/questions' }],
      '/admin/written': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Written Papers', path: '/admin/written' }],
      '/admin/picture-manager': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Picture Manager', path: '/admin/picture-manager' }],
      
      // Admin Tools
      '/admin/tools': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }],
      '/admin/tools/modules': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Module Activation', path: '/admin/tools/modules' }],
      '/admin/tools/audit-logs': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Audit Logs', path: '/admin/tools/audit-logs' }],
      '/admin/tools/settings': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Site Settings', path: '/admin/tools/settings' }],
      '/admin/tools/sessions': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Session Management', path: '/admin/tools/sessions' }],
      '/admin/tools/health': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Health Monitoring', path: '/admin/tools/health' }],
      '/admin/tools/embeddings': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Semantic Embeddings', path: '/admin/tools/embeddings' }],
      '/admin/tools/abbreviations': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Abbreviation Dictionary', path: '/admin/tools/abbreviations' }],
      '/admin/tools/ai-service': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'AI Service', path: '/admin/tools/ai-service' }],
      '/admin/tools/email': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Email Service', path: '/admin/tools/email' }],
      '/admin/tools/email/templates': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Email Service', path: '/admin/tools/email' }, { label: 'Templates', path: '/admin/tools/email/templates' }],
      '/admin/tools/notification-logs': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Notification Log', path: '/admin/tools/notification-logs' }],
      '/admin/tools/contributions': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Contribution Report', path: '/admin/tools/contributions' }],
      '/admin/tools/coin-credits': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Manual Coin Credit', path: '/admin/tools/coin-credits' }],
      '/admin/tools/coin-settings': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Coin Rewards Settings', path: '/admin/tools/coin-settings' }],
      '/admin/tools/review-settings': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Review Settings', path: '/admin/tools/review-settings' }],
      '/admin/tools/data-maintenance': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Data Maintenance', path: '/admin/tools/data-maintenance' }],
      '/admin/tools/user-activity': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'User Activity Log', path: '/admin/tools/user-activity' }],
      '/admin/tools/client-errors': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Client Errors', path: '/admin/tools/client-errors' }],
      '/admin/tools/docs-manager': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Docs Manager', path: '/admin/tools/docs-manager' }],
      '/admin/docs': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Docs', path: '/admin/docs' }],
      '/ask': [{ label: 'Ask', path: '/ask' }],
      '/admin/updates': [{ label: 'Admin', path: '/admin' }, { label: 'Admin Tools', path: '/admin/tools' }, { label: 'Updates', path: '/admin/updates' }],
      '/admin/page-builder': [{ label: 'Admin', path: '/admin' }, { label: 'Page Builder', path: '/admin/page-builder' }],
      '/admin/analytics': [{ label: 'Admin', path: '/admin' }, { label: 'Platform Analytics', path: '/admin/analytics' }],
      '/admin/topics': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Topic Management', path: '/admin/topics' }],
      '/admin/functions': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Function Management', path: '/admin/functions' }],
      '/admin/surveyors': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Surveyor Management', path: '/admin/surveyors' }],
      '/admin/bulk-import': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Bulk Import', path: '/admin/bulk-import' }],
      '/admin/bulk-import-review': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Bulk Import', path: '/admin/bulk-import' }, { label: 'Review', path: '/admin/bulk-import-review' }],
      '/admin/mmd': [{ label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'MMD Management', path: '/admin/mmd' }],
      
      // Admin User Management
      '/admin/user-management': [{ label: 'Admin', path: '/admin' }, { label: 'User Management', path: '/admin/user-management' }],
      '/admin/users': [{ label: 'Admin', path: '/admin' }, { label: 'User Management', path: '/admin/user-management' }, { label: 'Users', path: '/admin/users' }],
      '/admin/roles': [{ label: 'Admin', path: '/admin' }, { label: 'User Management', path: '/admin/user-management' }, { label: 'Roles', path: '/admin/roles' }],
      
      // Admin Finances
      '/admin/finances': [{ label: 'Admin', path: '/admin' }, { label: 'Finances', path: '/admin/finances' }],
      '/admin/finances/analytics': [{ label: 'Admin', path: '/admin' }, { label: 'Finances', path: '/admin/finances' }, { label: 'Analytics', path: '/admin/finances/analytics' }],
      '/admin/finances/logs': [{ label: 'Admin', path: '/admin' }, { label: 'Finances', path: '/admin/finances' }, { label: 'Payment Logs', path: '/admin/finances/logs' }],
      '/admin/finances/plans': [{ label: 'Admin', path: '/admin' }, { label: 'Finances', path: '/admin/finances' }, { label: 'Plans', path: '/admin/finances/plans' }],
      '/admin/finances/settings': [{ label: 'Admin', path: '/admin' }, { label: 'Finances', path: '/admin/finances' }, { label: 'Settings', path: '/admin/finances/settings' }],
    };
    
    // Check for exact match first
    if (breadcrumbMap[path]) {
      return path === '/' ? [] : [{ label: 'Home', path: '/' }, ...breadcrumbMap[path]];
    }
    
    // Handle dynamic routes
    if (path.startsWith('/written-bank/') && path !== '/written-bank/all') {
      return [{ label: 'Home', path: '/' }, { label: 'Dashboard', path: '/dashboard' }, { label: 'Written Question Bank', path: '/written-bank' }, { label: 'Paper Questions', path: path }];
    }
    if (path.startsWith('/admin/written/')) {
      return [{ label: 'Home', path: '/' }, { label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Written Papers', path: '/admin/written' }, { label: 'Paper Detail', path: path }];
    }
    if (path.startsWith('/admin/notes/flagged-reports')) {
      return [{ label: 'Home', path: '/' }, { label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: 'Flagged Reports', path: path }];
    }
    if (path.startsWith('/admin/notes/')) {
      const dept = path.split('/').pop();
      const deptLabel = dept === 'engineering' ? 'Engineering Notes' : 'Deck Notes';
      return [{ label: 'Home', path: '/' }, { label: 'Admin', path: '/admin' }, { label: 'Content', path: '/admin/content' }, { label: deptLabel, path: path }];
    }
    if (path.startsWith('/help/guides/')) {
      return [{ label: 'Home', path: '/' }, { label: 'Help', path: '/help' }, { label: 'Guides', path: '/help/guides' }, { label: 'Article', path: path }];
    }
    if (path.startsWith('/guidelines/')) {
      // Legacy article URL — keep breadcrumbs pointing at new canonical
      // pages so the whole trail feels consistent post-rename.
      return [{ label: 'Home', path: '/' }, { label: 'Help', path: '/help' }, { label: 'Guides', path: '/help/guides' }, { label: 'Article', path: path }];
    }
    // Live Classes — join/host rooms
    if (path.startsWith('/live-classes/') && path.endsWith('/join')) {
      return [{ label: 'Home', path: '/' }, { label: 'Dashboard', path: '/dashboard' }, { label: 'Live Classes', path: '/live-classes' }, { label: 'Join Class', path: path }];
    }
    if (path.startsWith('/live-classes/') && path.endsWith('/questions')) {
      return [{ label: 'Home', path: '/' }, { label: 'Dashboard', path: '/dashboard' }, { label: 'Live Classes', path: '/live-classes' }, { label: 'Q&A', path: path }];
    }
    if (path.startsWith('/live-classes/') && path.endsWith('/archive')) {
      return [{ label: 'Home', path: '/' }, { label: 'Dashboard', path: '/dashboard' }, { label: 'Live Classes', path: '/live-classes' }, { label: 'Archive', path: path }];
    }
    if (path.startsWith('/admin/live-classes/') && path.endsWith('/host')) {
      return [{ label: 'Home', path: '/' }, { label: 'Admin', path: '/admin' }, { label: 'Live Classes', path: '/admin/live-classes' }, { label: 'Host Class', path: path }];
    }
    
    // Fallback: just show Home for unknown routes (but not for home itself)
    return path === '/' ? [] : [{ label: 'Home', path: '/' }];
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header
        id="app-header"
        className="bg-white border-b border-gray-200 sticky top-0 z-50 shadow-sm"
      >
        <div className="container mx-auto px-4 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-2 cursor-pointer" onClick={() => navigate('/')}>
              <Anchor className="w-7 h-7 text-[#0A7EA4]" />
              <span className="text-2xl font-bold text-[#0A7EA4]">MARQ</span>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center gap-2">
              <Button
                data-testid="nav-home"
                variant="ghost"
                onClick={() => navigate('/')}
                className="text-gray-600 hover:text-[#0A7EA4]"
              >
                <Home className="w-4 h-4 mr-2" />
                Home
              </Button>
              <Button
                data-testid="nav-dashboard"
                variant="ghost"
                onClick={() => navigate('/dashboard')}
                className="text-gray-600 hover:text-[#0A7EA4]"
              >
                <BookOpen className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
              <Button
                data-testid="nav-submit"
                variant="ghost"
                onClick={() => navigate('/submit')}
                className="text-gray-600 hover:text-[#0A7EA4]"
              >
                <PlusCircle className="w-4 h-4 mr-2" />
                Submit
              </Button>
              <Button
                data-testid="nav-forum"
                variant="ghost"
                onClick={() => navigate('/forum')}
                className="text-gray-600 hover:text-[#0A7EA4]"
              >
                <MessageCircle className="w-4 h-4 mr-2" />
                Forum
              </Button>
              {user && (user.subscription_status === 'paid' || user.role === 'admin' || user.role === 'sub-admin') && !isModuleHidden('ask_semantic_search') && (
                <Button
                  data-testid="nav-ask"
                  variant="ghost"
                  onClick={() => navigate('/ask')}
                  className="text-gray-600 hover:text-[#0A7EA4]"
                >
                  <Search className="w-4 h-4 mr-2" />
                  Ask
                </Button>
              )}
              {isAdminOrSubAdmin() && (
                <Button
                  data-testid="nav-admin"
                  variant="ghost"
                  onClick={() => navigate('/admin')}
                  className="text-gray-600 hover:text-[#0A7EA4]"
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Admin
                </Button>
              )}
              {/* Help — single link to the /help hub which has tabs for
                  Help Guide + Guidelines. */}
              <Button
                variant="ghost"
                onClick={() => navigate('/help')}
                className="text-gray-600 hover:text-[#0A7EA4]"
                data-testid="nav-help"
              >
                <HelpCircle className="w-4 h-4 mr-2" />
                Help
              </Button>
              {/* Notification Bell */}
              <NotificationBell />
              <Button
                data-testid="nav-profile"
                variant="ghost"
                onClick={() => navigate('/profile')}
                className="text-gray-600 hover:text-[#0A7EA4]"
              >
                <User className="w-4 h-4 mr-2" />
                {user ? user.name : 'Profile'}
              </Button>
            </nav>

            {/* Mobile Hamburger Menu */}
            <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
              <SheetTrigger asChild className="lg:hidden">
                <Button variant="ghost" size="sm" data-testid="mobile-menu-btn">
                  <Menu className="w-6 h-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Anchor className="w-5 h-5 text-[#0A7EA4]" />
                    MARQ Menu
                  </SheetTitle>
                </SheetHeader>
                <nav className="flex flex-col gap-2 mt-6">
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <Home className="w-5 h-5 mr-3" />
                    Home
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/dashboard'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <BookOpen className="w-5 h-5 mr-3" />
                    Dashboard
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/submit'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <PlusCircle className="w-5 h-5 mr-3" />
                    Submit Question
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/forum'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <MessageCircle className="w-5 h-5 mr-3" />
                    Forum
                  </Button>
                  {user && (user.subscription_status === 'paid' || user.role === 'admin' || user.role === 'sub-admin') && !isModuleHidden('ask_semantic_search') && (
                    <Button
                      variant="ghost"
                      onClick={() => { navigate('/ask'); setMobileMenuOpen(false); }}
                      className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                    >
                      <Search className="w-5 h-5 mr-3" />
                      Ask
                    </Button>
                  )}
                  {isAdminOrSubAdmin() && (
                    <Button
                      variant="ghost"
                      onClick={() => { navigate('/admin'); setMobileMenuOpen(false); }}
                      className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                    >
                      <Shield className="w-5 h-5 mr-3" />
                      Admin Dashboard
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/help'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <HelpCircle className="w-5 h-5 mr-3" />
                    Help
                  </Button>
                  <Button
                    variant="ghost"
                    onClick={() => { navigate('/profile'); setMobileMenuOpen(false); }}
                    className="justify-start text-gray-600 hover:text-[#0A7EA4] hover:bg-[#0A7EA4]/10"
                  >
                    <User className="w-5 h-5 mr-3" />
                    {user ? user.name : 'Profile'}
                  </Button>
                </nav>
              </SheetContent>
            </Sheet>
          </div>

          {/* Breadcrumbs */}
          {!hideBreadcrumbs && breadcrumbs.length > 0 && (
            <div className="mt-4">
              <Breadcrumb>
                <BreadcrumbList>
                  {breadcrumbs.map((crumb, index) => (
                    <React.Fragment key={crumb.path}>
                      <BreadcrumbItem>
                        {index === breadcrumbs.length - 1 ? (
                          <BreadcrumbPage className="text-[#0A7EA4] font-medium">{crumb.label}</BreadcrumbPage>
                        ) : (
                          <BreadcrumbLink
                            onClick={() => navigate(crumb.path)}
                            className="cursor-pointer hover:text-[#0A7EA4]"
                          >
                            {crumb.label}
                          </BreadcrumbLink>
                        )}
                      </BreadcrumbItem>
                      {index < breadcrumbs.length - 1 && <BreadcrumbSeparator />}
                    </React.Fragment>
                  ))}
                </BreadcrumbList>
              </Breadcrumb>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 min-h-[calc(100vh-180px)]">
        {children}
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Layout;